import moment from "moment-timezone";
import { Meeting } from "@platform/types/platform.types";

export type Args = {
  meetings: Meeting[];
  options?: {
    anchorDate?: string;
  };
};

export function getClosestMeetingToCurrentTime({
  meetings,
  options = {},
}: Args) {
  if (!meetings || meetings.length === 0) return null;

  // default to current time unless anchorDate is provided
  const anchor = options.anchorDate ? moment(options.anchorDate) : moment();

  // convert to UTC
  const anchorTime = moment.tz(anchor, "UTC");

  // sort meetings by start time for looking ahead
  const byStart = [...meetings].sort(
    (a, b) => moment(a.start_time).valueOf() - moment(b.start_time).valueOf()
  );

  // sort meetings by end time for looking back
  const byEnd = [...meetings].sort(
    (a, b) => moment(a.end_time).valueOf() - moment(b.end_time).valueOf()
  );

  // get the first and last meetings
  const firstMeeting = byStart[0];
  const lastMeeting = byEnd[byEnd.length - 1];

  // Clamp to first meeting if anchor is before the first meeting
  if (anchorTime.isBefore(moment(firstMeeting.start_time))) {
    return firstMeeting;
  }

  // Clamp to last meeting if anchor is after the last meeting
  if (anchorTime.isAfter(moment(lastMeeting.end_time))) {
    return lastMeeting;
  }

  // Check if there is a meeting that is about to start (within 60 minutes)
  const aboutToStart = byStart.find((m) => {
    const start = moment(m.start_time);
    const diffMinutes = start.diff(anchorTime, "minutes");
    return diffMinutes >= 0 && diffMinutes <= 60;
  });

  // Return meeting that is about to start if anchor is within 60 minutes of its start time
  if (aboutToStart) return aboutToStart;

  // Check if anchor is inside an ongoing meeting
  // Account for overlapping meetings by looking backwards by end time
  const ongoing = byEnd.reverse().find((m) => {
    const start = moment(m.start_time);

    // Add 24h grace to the end time
    const end = moment(m.end_time).add(24, "hours");

    return anchorTime.isSameOrAfter(start) && anchorTime.isBefore(end);
  });

  // Return ongoing meeting if anchor is inside an ongoing meeting
  if (ongoing) return ongoing;

  // Return next upcoming meeting if anchor is after the last meeting
  const upcoming = byStart.find((m) =>
    moment(m.start_time).isSameOrAfter(anchorTime)
  );

  if (upcoming) return upcoming;

  return null;
}

export default getClosestMeetingToCurrentTime;
